<template>
  <Tab no-help @close="removeCurrent">
    <template #toolbar-help>
      <v-btn class="tw-mr-2" fab text x-small :title="$t('dashboard.help.show')" @click="showHelp(menu,page)">
            <span class="mr-1">
              <v-icon color="primary" x-small>fa fa-question</v-icon>
            </span>
      </v-btn>
    </template>
    <template #toolbar-items>
      <!-- ------------------------ list ------------------------ -->
      <TabToolbarButton
        v-model="page"
        :menu="menu"
        :selected="page === 'list'"
        icon="fa-list"
        name="list"
      ></TabToolbarButton>
    </template>
    <template #container>
      <!-- ------------------------ list ------------------------ -->
      <TabPage :menu="menu" :page="page" name="list" no-documentation>
        <template #content>
          <v-row>
            <!-- Stories list -->
            <v-col cols="12" lg="4" md="6">
              <WebsiteWrapper @selected="selectedWebsite = $event.id" :verified="2" :plan="0" mandatory>
                <v-card>
                  <v-container>
                    <span class="sr-title">
                      <!-- List title -->
                      {{ $t("stories.manager.list.title") }}
                    </span>
                    <div class="float-right" style="position:relative; top:-5px;">
                      <v-btn :title="$t('stories.manager.list.reload')" color="primary" icon @click="fetchStories(selectedWebsite)">
                        <v-icon small>fas fa-sync-alt</v-icon>
                      </v-btn>
                    </div>
                  </v-container>
                  <v-card-text v-if="stories.length > 0" class="pa-0">
                    <v-list subheader three-line>
                      <!-- Scrollable list of stories -->
                      <v-list-item-group
                        v-model="selectedStory"
                        active-class="blue--text"
                        style="height: calc(100vh - 300px);overflow-y: auto;"
                      >
                        <v-list-item v-for="story in stories" :key="story.id + story.database">
                          <v-list-item-avatar v-if="story.avatar">
                            <v-img :alt="`${story.user_name} avatar`" :src="story.avatar"></v-img>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>
                              <a
                                v-if="getName(story) !== ''"
                                :title="getUserName(story)"
                                class="blue--text"
                                v-html="getName(story)"
                              ></a>
                              <a
                                v-if="getName(story) === '' && getUserName(story) !== ''"
                                class="blue--text"
                                v-text="getUserName(story)"
                              ></a>
                              <a
                                v-if="getName(story) === '' && getUserName(story) === ''"
                                class="blue--text"
                                v-text="getOrigin(story)"
                              ></a>
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="story.content.hasOwnProperty('html')">
                              {{ renderStoryHtmlToText(story.content.html) }}
                            </v-list-item-subtitle>
                            <v-list-item-subtitle>
                              <template>
                                <v-chip
                                  v-if="story.is_moderate === true && story.confidence >= 40"
                                  :title="$t('stories.manager.list.moderation')"
                                  class="mr-1 white--text"
                                  color="green"
                                  x-small
                                >
                                  <v-icon left x-small>
                                    fa-user-check
                                  </v-icon>
                                  {{ $t("stories.manager.list.published") }}
                                </v-chip>
                                <v-chip
                                  v-else-if="story.is_moderate === true && story.confidence <= 10"
                                  :title="$t('stories.manager.list.moderation')"
                                  class="mr-1 white--text"
                                  color="red"
                                  x-small
                                >
                                  <v-icon left x-small>
                                    fa-user-check
                                  </v-icon>
                                  {{ $t("stories.manager.list.rejected") }}
                                </v-chip>
                                <v-chip
                                  v-else
                                  :title="$t('stories.manager.list.moderation')"
                                  class="mr-1 white--text"
                                  color="blue"
                                  x-small
                                >
                                  <v-icon left x-small>
                                    fa-user-check
                                  </v-icon>
                                  {{ $t("stories.manager.list.waiting") }}
                                </v-chip>
                              </template>
                              <v-chip :title="$t('stories.manager.list.created_at')" class="mr-1" x-small>
                                <v-icon color="black" left x-small>fa-history</v-icon>
                                {{ story.created_at | moment("from") }}
                              </v-chip>
                              <v-chip :title="$t('stories.manager.list.confidence')" class="mr-1" x-small>
                                <v-icon :color="confidenceColor(story.confidence)" left x-small>
                                  fa-burn
                                </v-icon>
                                {{ story.confidence }}%
                              </v-chip>
                              <v-chip :title="$t('stories.manager.list.likes_counter')" class="mr-1" x-small>
                                <v-icon color="blue" left x-small>
                                  fa-thumbs-up
                                </v-icon>
                                {{ story.likes_counter }}
                              </v-chip>
                              <v-chip
                                :title="$t('stories.manager.list.host')"
                                class="mr-1"
                                x-small
                                @click="openPageWhereStoryHasBeenPosted(story.full_path)"
                              >
                                <v-icon color="black" left x-small>
                                  fa-globe
                                </v-icon>
                                {{ story.host }}
                              </v-chip>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                  </v-card-text>
                  <v-card-text v-else-if="stories.length <= 0 && selectedWebsite">
                    {{ $t("stories.manager.list.nothing") }}
                  </v-card-text>
                </v-card>
              </WebsiteWrapper>
            </v-col>
            <v-col v-if="story" cols="12" lg="8" md="6">
              <v-stepper v-model="step" class="elevation-0">
                <v-stepper-items>
                  <!-- Step render story -->
                  <v-stepper-content :step="1" class="pa-0">
                    <v-card>
                      <v-row v-if="story.content.hasOwnProperty('render')">
                        <v-col cols="8" style="max-height: calc(100vh - 96px);">
                          <v-container>
                            <div class="tw-mb-2 tw-font-semibold">{{ $t("stories.manager.editor.render") }}</div>
                            <div
                              class="sr-story-render"
                              style="max-height: calc(100vh - 200px);"
                              v-html="story.content.render"
                            ></div>
                          </v-container>
                        </v-col>
                        <v-col cols="4">
                          <v-card-actions>
                            <v-btn class="mr-1 mt-10" color="primary" small @click="step = 2">
                              {{ $t("stories.manager.render.edit") }}
                            </v-btn>
                            <v-btn
                              v-if="story.is_moderate !== true"
                              class="mr-1 mt-10"
                              color="primary"
                              small
                              @click="step = 3"
                            >
                              {{ $t("stories.manager.render.publish") }}
                            </v-btn>
                          </v-card-actions>
                          <v-card-text v-if="story.is_moderate === true">
                            <v-alert v-if="story.confidence <= 10" border="left" color="red" type="warning">
                              <i>{{ $t("stories.manager.render.rejected") }}</i>
                              <br />
                              <i
                                >{{ $t("stories.manager.render.reason") }} : {{ reasons[story.confidence - 1].text }}</i
                              >
                            </v-alert>
                            <v-alert v-else border="left" color="green" type="success">
                              <i>{{ $t("stories.manager.render.published") }}</i>
                            </v-alert>
                          </v-card-text>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-stepper-content>
                  <!-- Step modify story -->
                  <v-stepper-content :step="2" class="pa-0">
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-card class="mx-auto">
                          <v-card-text style="max-height: calc(100vh - 200px); overflow-y: auto">
                            <template v-if="story.content.hasOwnProperty('template')">
                              <div class="tw-mb-2 tw-font-semibold">{{ $t("stories.manager.editor.template") }}</div>
                              <v-text-field v-model="editorTemplate" readonly></v-text-field>
                            </template>
                            <template v-if="story.content.hasOwnProperty('image')">
                              <div class="tw-mb-2 tw-font-semibold">{{ $t("stories.manager.editor.image") }}</div>
                              <v-text-field v-model="story.content.image" readonly></v-text-field>
                            </template>
                            <template v-if="story.content.hasOwnProperty('html')">
                              <div class="tw-mb-2 tw-font-semibold">{{ $t("stories.manager.editor.html") }}</div>
                              <ckeditor
                                v-model="editorContent"
                                :config="editorConfig"
                                :editor="editor"
                                @input="renderStory"
                              ></ckeditor>
                            </template>
                          </v-card-text>
                          <v-card-actions class="mt-3">
                            <v-btn class="mr-1" color="primary" small @click="saveStoryModifications"
                              >{{ $t("stories.manager.editor.save") }}
                            </v-btn>
                            <v-btn color="default" small @click="cancelStoryModifications"
                              >{{ $t("stories.manager.editor.cancel") }}
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <template
                          v-if="story.content.hasOwnProperty('render')"
                          style="max-height: calc(100vh - 160px);"
                        >
                          <div class="tw-mb-2 tw-font-semibold">{{ $t("stories.manager.editor.render") }}</div>
                          <div class="sr-story-render" v-html="story.content.render"></div>
                        </template>
                      </v-col>
                    </v-row>
                  </v-stepper-content>
                  <!-- Step publish/reject story -->
                  <v-stepper-content :step="3" class="pa-0">
                    <v-card color="tw-mt-4">
                      <v-container>
                        <!-- Documentation button -->
                        <DocumentationDialogue class="tw-inline" path="manager/moderation/story" :key="$i18n.locale"/>

                        <span class="sr-title">
                          {{ $t("stories.manager.response.title") }}
                        </span>

                        <!-- Content of the response card -->
                        <v-card-text>
                          <v-stepper v-model="stepPublish" class="elevation-0">
                            <v-stepper-items>
                              <!-- Step 1 => Make a choice -->
                              <v-stepper-content :step="1" class="pa-0">
                                <div class="tw-mt-2">
                                  <v-btn class="tw-mx-2" color="primary" small @click="step = 1">
                                    {{ $t("stories.manager.response.buttons.start") }}
                                  </v-btn>
                                  <v-btn class="tw-mx-2" color="primary" small @click="stepPublish = 2">
                                    {{ $t("stories.manager.response.buttons.publish") }}
                                  </v-btn>
                                  <v-btn class="tw-mx-2" color="error" small @click="stepPublish = 3">
                                    {{ $t("stories.manager.response.buttons.report") }}
                                  </v-btn>
                                </div>
                              </v-stepper-content>

                              <!-- Step 2 => Publish the story -->
                              <v-stepper-content :step="2" class="pa-0">
                                <span class="font-weight-bold">{{ $t("stories.manager.response.publish") }}</span>
                                <v-slider
                                  v-model="pertinence"
                                  :label="$t('stories.manager.response.pertinence')"
                                  :thumb-color="pertinenceColor"
                                  class="mt-6"
                                  color="grey darken-1"
                                  max="9"
                                  min="1"
                                  thumb-label="always"
                                  thumb-size="20"
                                  track-color="grey lighten-1"
                                />
                                <v-btn class="mr-4" small @click="stepPublish = 1">
                                  {{ $t("stories.manager.response.buttons.back") }}
                                </v-btn>
                                <v-btn color="primary" small @click="publish">
                                  {{ $t("stories.manager.response.buttons.publish") }}
                                </v-btn>
                              </v-stepper-content>

                              <v-stepper-content :step="3" class="pa-0">
                                <span class="font-weight-bold">
                                  {{ $t("stories.manager.response.report.title") }}
                                </span>
                                <v-select
                                  v-model="reason"
                                  :items="reasons"
                                  :label="$t('stories.manager.response.report.label')"
                                  class="mt-2"
                                  item-text="text"
                                  item-value="value"
                                />
                                <v-btn class="mt-2 mr-4" small @click="stepPublish = 1">
                                  {{ $t("stories.manager.response.buttons.back") }}
                                </v-btn>
                                <v-btn :disabled="reason === 0" class="mt-2" color="primary" small @click="report">
                                  {{ $t("stories.manager.response.buttons.report") }}
                                </v-btn>
                              </v-stepper-content>
                            </v-stepper-items>
                          </v-stepper>
                        </v-card-text>
                      </v-container>
                    </v-card>
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
            </v-col>
          </v-row>
        </template>
      </TabPage>
    </template>
  </Tab>
</template>

<script>
import tab from "../../../../mixins/tab"
import stories from "../../../../mixins/stories"
import ClassicEditor from "ckeditor5-build-custom"
import { Types as TypesDialog } from "../../../../store/modules/dialog"
import { Types as TypesSnackbar } from "../../../../store/modules/snackbar"
import WebsiteWrapper from "@/components/common/Form/WebsiteWrapper"

export default {
  name: "Stories",
  components: {
    WebsiteWrapper,
  },
  mixins: [tab, stories],
  data: () => ({
    pages: ["list"],
    menu: "stories",
    page: "list",
    step: 1,
    story: null,
    selectedStory: null,
    stories: [],
    selectedWebsite: null,
    editorMode: false,
    editor: ClassicEditor,
    editorContent: null,
    editorConfig: {
      language: "fr",
      toolbar: [
        "heading",
        "|",
        "bold",
        "italic",
        "|",
        "alignment",
        "|",
        "fontColor",
        "fontBackgroundColor",
        "|",
        "link",
        "|",
        "bulletedList",
        "numberedList",
      ],
    },
    editorTemplates: [
      { value: "photo-first", text: "L'image puis le texte" },
      { value: "text-first", text: "Le texte puis l'image" },
      { value: "text-over-image", text: "Le texte sur l'image" },
    ],
    editorTemplate: "photo-first",
    pertinence: 5,
    reasons: [],
    reason: 0,
    stepPublish: 1,
  }),
  mounted() {
    this.reasons = [
      {
        value: 1,
        text: this.$t("stories.manager.response.report.1"),
      },
      {
        value: 2,
        text: this.$t("stories.manager.response.report.2"),
      },
      {
        value: 3,
        text: this.$t("stories.manager.response.report.3"),
      },
      {
        value: 4,
        text: this.$t("stories.manager.response.report.4"),
      },
      {
        value: 5,
        text: this.$t("stories.manager.response.report.5"),
      },
      {
        value: 6,
        text: this.$t("stories.manager.response.report.6"),
      },
      {
        value: 7,
        text: this.$t("stories.manager.response.report.7"),
      },
      {
        value: 8,
        text: this.$t("stories.manager.response.report.8"),
      },
      {
        value: 9,
        text: this.$t("stories.manager.response.report.9"),
      },
    ]
  },
  watch: {
    selectedWebsite(value) {
      this.fetchStories(value)
    },
    selectedStory(value) {
      if (value !== undefined) {
        this.resetSteppers()
        this.story = this.stories[value]
        this.editorContent = this.story.content.html
        this.renderStory()
      } else {
        this.story = null
      }
    },
    editorContent() {
      this.renderStory()
      this.refreshStoriesList()
    },
  },
  computed: {
    updateDate() {
      return this.$moment(this.story.updated_at).from()
    },
    pertinenceColor() {
      switch (this.pertinence) {
        case 1:
          return "red darken-3"
        case 2:
          return "red"
        case 3:
          return "orange darken-3"
        case 4:
          return "orange"
        case 5:
          return "orange lighten-2"
        case 6:
          return "green darken-3"
        case 7:
          return "green darken-1"
        case 8:
          return "green"
        case 9:
          return "green lighten-2"
        default:
          return "grey"
      }
    },
  },
  methods: {
    getUserName(story) {
      const { user_name } = story
      const semicolonPos = user_name.indexOf(":")
      if (semicolonPos > 0) {
        return ""
      } else {
        return `${user_name}`
      }
    },
    getName(story) {
      const { first_name } = story
      const { last_name } = story
      let name = ""
      if (first_name) name = name + " " + first_name
      if (last_name) name = name + " " + last_name
      return name.trim()
    },
    displayOrigin(story) {
      return this.$t("origin.source") + this.getOrigin(story)
    },
    getOrigin(story) {
      const securating = "Securating"
      const { user_name } = story
      const semicolonPos = user_name.indexOf(":")
      if (semicolonPos > 0) {
        const prefix = user_name.substr(0, semicolonPos)

        switch (prefix) {
          case "anonymous":
            return securating
          default:
            return `${this.$t("origin." + prefix)}`
        }
      } else {
        return securating
      }
    },
    refreshStoriesList() {
      this.stories[this.selectedStory] = this.story
    },
    resetSteppers() {
      this.step = 1
      this.stepPublish = 1
      this.pertinence = 5
      this.reason = 0
    },
    publish() {
      this.$store.dispatch(TypesDialog.actions.CONFIRM, {
        title: this.$t("stories.manager.dialogs.publish.title"),
        message: this.$t("stories.manager.dialogs.publish.text"),
        callback: () => {
          let story = { ...this.story }
          this.story.confidence = story.confidence = 40 + this.pertinence
          this.story.relevance = story.relevance = this.pertinence
          this.$axios.post("office/manager/story/publish", story).then(() => {
            this.story.is_moderate = story.is_moderate = true
            this.refreshStoriesList()
            this.resetSteppers()
          })
        },
      })
    },
    report() {
      this.$store.dispatch(TypesDialog.actions.CONFIRM, {
        title: this.$t("stories.manager.dialogs.report.title"),
        message:
          this.$t("stories.manager.dialogs.report.text") +
          `<br>` +
          `<strong>${this.reasons.find(r => r.value === this.reason).text}</strong>`,
        callback: () => {
          let story = { ...this.story }
          this.story.confidence = story.confidence = this.reason
          this.story.relevance = story.relevance = this.reason
          this.$axios.post("office/manager/story/report", story).then(() => {
            this.story.is_moderate = story.is_moderate = true
            this.refreshStoriesList()
            this.resetSteppers()
          })
        },
      })
    },
    saveStoryModifications() {
      this.story.content.html = this.editorContent
      let story = { ...this.story }
      story.content = JSON.stringify(story.content)
      this.$axios
        .patch("office/manager/story", story)
        .then(() => {
          this.step = 3
          this.stepPublish = 1
        })
        .catch(() => this.handleError("stories.manager.error.patch_story"))
    },
    cancelStoryModifications() {
      this.editorContent = this.story.content.html
      this.resetSteppers()
    },
    handleError(i18n = "") {
      this.$store.dispatch(TypesSnackbar.actions.PUSH_MESSAGE, {
        lines: this.$t(i18n),
        config: { timeout: 1000 * 6, color: "error" },
      })
    },
    fetchStories(website_id) {
      this.stories = [];
      this.$axios
        .get("office/manager/stories", { params: { website_id } })
        .then(response => {
          this.stories = response.data.reduce((accumulator, story) => {
            story.content = JSON.parse(story.content)
            accumulator.push(story)
            return accumulator
          }, [])
        })
        .catch(() => this.handleError("stories.manager.error.fetch_stories"))
    },
    confidenceColor(confidence) {
      if (confidence <= 5) {
        return "red darken-3"
      } else if (confidence <= 10) {
        return "red"
      } else if (confidence <= 20) {
        return "orange darken-3"
      } else if (confidence <= 30) {
        return "orange"
      } else if (confidence <= 40) {
        return "orange lighten-2"
      } else if (confidence <= 50) {
        return "green darken-3"
      } else if (confidence <= 60) {
        return "green darken-2"
      } else if (confidence <= 70) {
        return "green darken-1"
      } else if (confidence <= 80) {
        return "green"
      } else if (confidence <= 90) {
        return "green lighten-2"
      } else if (confidence <= 100) {
        return "green lighten-2"
      } else {
        return "red darken-4"
      }
    },
    openPageWhereStoryHasBeenPosted(url) {
      window.open(url, "_blank")
    },
  },
}
</script>
